import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCheck, Trash2, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import Header from "../../layouts/Header";
import notificationIcon from "../../assets/icons/no_notifications.svg";
import { useQueryClient } from "@tanstack/react-query";
import {
  deleteNotificationsAll,
  markAsReadService,
  deleteNotificationService,
} from "../../services/notificationService";
import { useNotifications } from "../../hooks/query/queries";
import withdrawIcon from "../../assets/images/withdrawaIcon.svg";
import buyIcon from "../../assets/images/buyIcon.svg";
import sendIcon from "../../assets/images/sendIcon.svg";
import zarDeposit from "../../assets/images/zarDeposit.svg";
import sellIcon from "../../assets/images/sellIcon.svg";
import receiveIcon from "../../assets/images/receiveIcon.svg";
import { Skeleton } from "../../components/ui/skeleton";
import { useNotificationStore } from "../../store/useNotifications";
import NotificationDetailModal from "../../components/ui/notification-detail-modal";
// Icon mapping based on notification icon type or notification type
const getNotificationIcon = (notification) => {
  const iconMap = {
    // Icon values
    send: sendIcon,
    receive: receiveIcon,
    buy: buyIcon,
    sell: sellIcon,
    deposit: zarDeposit,
    withdraw: withdrawIcon,
    pay_sent: sendIcon,
    pay_received: receiveIcon,
    price_up: receiveIcon,
    price_down: sendIcon,
    error: withdrawIcon,
    autostack: buyIcon,
    autostack_error: withdrawIcon,
    autostack_reminder: buyIcon,
    default: zarDeposit,
    // Direct type mappings (fallback)
    funds_sent: sendIcon,
    funds_received: receiveIcon,
    purchase_confirmed: buyIcon,
    sale_complete: sellIcon,
    deposit_successful: zarDeposit,
    withdrawal_successful: withdrawIcon,
    payment_sent: sendIcon,
    payment_received: receiveIcon,
    price_alert_up: buyIcon,
    price_alert_down: sellIcon,
    transaction_failed: withdrawIcon,
    autostack_created: buyIcon,
    autostack_triggered: buyIcon,
    autostack_failed: withdrawIcon,
    autostack_edited: buyIcon,
    autostack_paused: sellIcon,
    autostack_resumed: buyIcon,
  };

  // Try displayIcon first, then icon, then type, then default
  const iconKey =
    notification?.displayIcon || notification?.icon || notification?.type;
  const iconEntries = Object.entries(iconMap);
  const found = iconEntries.find(([key]) => key === iconKey);
  return found ? found[1] : iconMap.default;
};

// Category filters
const filters = [
  { key: "all", label: "All" },
  { key: "funds", label: "Funds" },
  { key: "trading", label: "Trading" },
  { key: "fiat", label: "Deposits" },
  { key: "transaction", label: "Transactions" },
  { key: "price_alert", label: "Alerts" },
  { key: "autostack", label: "AutoStack" },
  { key: "pay", label: "88Pay" },
];

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const { data: notis, isLoading: loadingNotis } = useNotifications(
    1,
    50,
    selectedFilter === "all" ? null : selectedFilter
  );

  const setNotifications = useNotificationStore((s) => s.setNotifications);
  const markAsReadStore = useNotificationStore((s) => s.markAsRead);
  useEffect(() => {
    if (notis?.results) {
      setNotifications(notis.results, notis.totalUnread);
    }
  }, [notis, setNotifications]);

  const queryClient = useQueryClient();

  // Results are already filtered by the API when a category is selected
  const filteredNotifications = notis?.results || [];

  const deleteClearAllCall = async () => {
    setIsLoading(true);
    try {
      const apiCall = await deleteNotificationsAll();
      if (apiCall) {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }
    } catch (err) {
      console.log("error:", err);
    } finally {
      setIsLoading(false);
    }
  };
  const handleMarkAsRead = async (id) => {
    try {
      const mark = await markAsReadService(id);
      if (mark) {
        markAsReadStore(id);
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }
      return mark;
    } catch (error) {
      console.log("error:", error);
    }
  };
  const handleDelete = async (id) => {
    try {
      const deleteNoti = await deleteNotificationService(id);
      if (deleteNoti) {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }
      return deleteNoti;
    } catch (error) {
      console.log("error:", error);
    }
  };
  const hasNotifications = notis?.results?.length > 0;

  return (
    <>
      <Header />

      <div className="bg-gray-100 min-h-screen">
        <div className="max-w-3xl mx-auto py-10 px-6">
          <h2 className="text-center text-2xl font-bold text-purple-700 mb-6">
            Your latest updates are here
          </h2>

          {/* Filters + Clear all */}
          <div className="flex items-center justify-between mb-6 gap-2">
            <div className="flex items-center gap-2">
              {filters.map((filter) => (
                <Button
                  key={filter.key}
                  className={cn(
                    "rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap",
                    selectedFilter === filter.key
                      ? "bg-purple-700 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  )}
                  onClick={() => setSelectedFilter(filter.key)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
            <Button
              onClick={() => deleteClearAllCall()}
              disabled={isLoading}
              className="text-rose-600 font-semibold bg-transparent p-2 text-xs hover:bg-white/70 rounded-full hover:underline whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {isLoading && <Loader2 className="animate-spin w-3 h-3" />}
              {isLoading ? "Clearing" : "Clear all"}
            </Button>
          </div>

          {/* Unread count */}
          {notis?.totalUnread > 0 && (
            <div className="mb-4 text-sm text-gray-600">
              {notis.totalUnread} unread notification
              {notis.totalUnread !== 1 ? "s" : ""}
            </div>
          )}

          {/* Notifications List */}
          <div className="space-y-4">
            {loadingNotis ? (
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center space-x-4 p-4 bg-white rounded-lg shadow-sm"
                  >
                    <Skeleton className="h-16 w-16 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : hasNotifications ? (
              <div className="space-y-4">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification._id}
                    onClick={() => {
                      if (!notification.isRead) {
                        handleMarkAsRead(notification._id);
                      }
                      setSelectedNotification(notification);
                      setIsDetailModalOpen(true);
                    }}
                    className={cn(
                      "relative flex items-start bg-white border rounded-lg p-4 shadow-sm transition hover:bg-neutral-50 group cursor-pointer",
                      !notification.isRead && "border-purple-400"
                    )}
                  >
                    {!notification.isRead && (
                      <span className="absolute top-2 left-2 h-2.5 w-2.5 rounded-full bg-purple-900" />
                    )}

                    {/* Icon */}
                    <div className="w-16 h-16 flex-shrink-0">
                      <img
                        src={getNotificationIcon(notification)}
                        alt={notification.title}
                        width={64}
                        height={64}
                        className="rounded-lg object-cover"
                      />
                    </div>

                    {/* Content */}
                    <div className="ml-4 flex-1 min-w-0">
                      <p className="text-xs text-gray-500">
                        {notification.displayLabel ||
                          notification.category
                            ?.replace(/_/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase()) ||
                          "Notification"}
                      </p>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {notification.title}
                      </h3>
                      <p className="text-xs text-gray-600 break-words pr-16">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {notification.formattedDate || notification.createdAt}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="absolute right-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification._id);
                        }}
                        className="p-1.5 rounded-full bg-transparent hover:bg-green-100 text-green-600 transition"
                        title="Mark as Read"
                        disabled={notification.isRead}
                      >
                        <CheckCheck size={16} />
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notification._id);
                        }}
                        className="p-1.5 rounded-full bg-transparent hover:bg-rose-100 text-rose-600 transition"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center py-16">
                <img
                  src={notificationIcon}
                  alt="No notifications"
                  width={150}
                  height={150}
                  className="mb-6"
                />
                <p className="text-lg font-semibold text-brand-primary mb-2">
                  {selectedFilter === "all"
                    ? "You're all caught up"
                    : "No notifications in this category"}
                </p>
                <p className="text-sm text-text-primary">
                  {selectedFilter === "all"
                    ? "New notifications will appear here."
                    : "Try selecting a different filter."}
                </p>
              </div>
            )}
          </div>

          <div className="mt-10 text-center text-sm text-gray-500">
            <p>
              Questions?{" "}
              <a
                href="/support/index"
                className="text-rose-600 font-semibold cursor-pointer hover:underline"
              >
                Contact us
              </a>
            </p>
          </div>
        </div>
      </div>

      <NotificationDetailModal
        notification={selectedNotification}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedNotification(null);
        }}
        onGoToWallet={() => {
          setIsDetailModalOpen(false);
          setSelectedNotification(null);
          navigate("/trading/digitalWallet");
        }}
      />
    </>
  );
};

export default NotificationsPage;
